import React from 'react';
import './DeviceFrame.css';

export type DeviceId = 'macbook' | 'ipad' | 'iphone';

interface DeviceSpec {
  id: DeviceId;
  width: number;
  height: number;
  bezel: number;
  chromeBelow: number;
}

const DEVICE_SPECS: Record<DeviceId, DeviceSpec> = {
  macbook: { id: 'macbook', width: 1152, height: 720, bezel: 9, chromeBelow: 14 },
  ipad: { id: 'ipad', width: 834, height: 1112, bezel: 14, chromeBelow: 0 },
  iphone: { id: 'iphone', width: 402, height: 874, bezel: 10, chromeBelow: 0 },
};

const CAMERA_STRIP = 14;
const MACBOOK_BASE_OVERHANG = 26;

export function frameSize(device: DeviceId): { width: number; height: number } {
  const spec = DEVICE_SPECS[device];
  const cameraStrip = device === 'macbook' ? CAMERA_STRIP : 0;
  const baseOverhang = device === 'macbook' ? MACBOOK_BASE_OVERHANG * 2 : 0;
  return {
    width: spec.width + spec.bezel * 2 + baseOverhang,
    height: spec.height + spec.bezel * 2 + spec.chromeBelow + cameraStrip,
  };
}

interface Props {
  device: DeviceId;
  scale: number;
  hidden?: boolean;
  children: React.ReactNode;
}

 
export const DeviceFrame: React.FC<Props> = ({
  device,
  scale,
  hidden,
  children,
}) => {
  const spec = DEVICE_SPECS[device];
  const size = frameSize(device);
  const screenStyle = { width: spec.width, height: spec.height };

  return (
    <div
      className="device-frame"
      style={{
        width: size.width * scale,
        height: size.height * scale,
        visibility: hidden ? 'hidden' : undefined,
      }}
    >
      <div
        className="device-inner"
        style={{
          width: size.width,
          height: size.height,
          transform: `scale(${scale})`,
        }}
      >
        {device === 'macbook' && (
          <div key="macbook" className="device-macbook device-chassis">
            <div className="device-macbook-lid">
              <div className="device-macbook-camera" />
              <div
                className="device-screen device-macbook-screen"
                style={screenStyle}
              >
                {children}
              </div>
            </div>
            <div className="device-macbook-base" />
          </div>
        )}

        {device === 'ipad' && (
          <div key="ipad" className="device-ipad device-chassis">
            <div className="device-ipad-camera" />
            <div
              className="device-screen device-ipad-screen"
              style={screenStyle}
            >
              {children}
            </div>
          </div>
        )}

        {device === 'iphone' && (
          <div key="iphone" className="device-iphone device-chassis">
            <span className="device-button-left device-silence-switch" />
            <span className="device-button-left device-volume-up" />
            <span className="device-button-left device-volume-down" />
            <span className="device-button-right" />
            <div className="device-island" />
            <div
              className="device-screen device-iphone-screen"
              style={screenStyle}
            >
              {children}
            </div>
            <div className="device-home-indicator" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceFrame;
